<template>
  <v-row dense>
    <v-col cols="12" md="6">
      <v-text-field
        v-model="title"
        :rules="computedRules"
        label="Name *"
        maxlength="255"
        @input="handleTitleInput"
      ></v-text-field>
      <v-text-field
        v-model="number"
        label="Number"
        v-mask="farmNumberMask"
      ></v-text-field>
      <v-text-field
        v-model="latitude"
        label="Latitude"
        v-mask="latitudeMask"
      ></v-text-field>
      <v-text-field
        v-model="longitude"
        label="Longitude"
        v-mask="longitudeMask"
      ></v-text-field>
      <PhoneInput
        v-model="phone"
        :country-id.sync="country_id"
        :rules="rules.phone"
      />
      <v-text-field
        v-model="email"
        label="Email"
        maxlength="100"
        :rules="rules.email"
      ></v-text-field>
      <v-checkbox v-model="is_active" label="Active"></v-checkbox>
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model="address_1"
        :rules="rules.address"
        label="Address 1"
      ></v-text-field>
      <v-text-field
        v-model="address_2"
        :rules="rules.address"
        label="Address 2"
      ></v-text-field>
      <v-text-field
        v-model="city"
        :rules="rules.address"
        label="City"
      ></v-text-field>
      <v-select
        v-model="country_id"
        :items="countries"
        item-value="id"
        item-text="country_name"
        label="Country"
        no-data-text="No Countries Available"
      >
      </v-select>
      <v-select
        v-if="filterById('states', country_id, 'country_id').length"
        v-model="state_id"
        :items="filterById('states', country_id, 'country_id')"
        item-value="id"
        item-text="title"
        label="State/Province"
        no-data-text="No States/Provinces Available"
      >
      </v-select>
      <v-text-field
        v-model="postal_code"
        label="Postal Code"
        :rules="rules.postal_code"
      ></v-text-field>
    </v-col>
  </v-row>
</template>

<script src="https://unpkg.com/vue-tel-input@5"></script>
<script>
import Vue from "vue";
import rules from "@/utils/rules";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";
import hasIn from "lodash.hasin";
import PhoneInput from "@/components/shared/PhoneInput";
import VueTelInputVuetify from "vue-tel-input-vuetify/lib/vue-tel-input-vuetify.vue";

const latitudeMask = createNumberMask({
  prefix: "",
  suffix: "",
  allowDecimal: true,
  includeThousandsSeparator: false,
  integerLimit: 2,
  decimalLimit: 8,
  allowNegative: true,
  allowLeadingZeroes: true
});

const longitudeMask = createNumberMask({
  prefix: "",
  suffix: "",
  allowDecimal: true,
  includeThousandsSeparator: false,
  integerLimit: 3,
  decimalLimit: 8,
  allowNegative: true,
  allowLeadingZeroes: true
});

export default {
  name: "FarmsEditFormFields",
  components: { PhoneInput },
  data() {
    return {
      loading: true,
      formSubmitted: false,
      localTitleError: "",
      rules: {
        required: [rules.required],
        latitude: [rules.maxLength(11)],
        longitude: [rules.maxLength(12)],
        postal_code: [rules.maxLength(10)],
        address: [rules.maxLength(255)],
        email: [rules.email],
        phone: [
          v =>
            /^\s*[-()0-9+ ]+\s*$/.test(v) ||
            v === "" ||
            "Invalid phone number (digits only allowed). Keep adding digits to remove invalid characters"
        ]
      },
      latitudeMask,
      longitudeMask,
      farmNumberMask: "#################",
      phoneData: {
        number: "",
        valid: false,
        country: undefined
      }
    };
  },
  props: {
    backendErrors: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    ...mapGetters("baseData", ["countries", "states", "filterById"]),
    ...mapGetters("clients", ["selectedClient"]),
    ...mapFields("farms", [
      "detailItem.id",
      "detailItem.title",
      "detailItem.number",
      "detailItem.is_active",
      "detailItem.client_id",
      "detailItem.address_1",
      "detailItem.address_2",
      "detailItem.city",
      "detailItem.country_id",
      "detailItem.state_id",
      "detailItem.postal_code",
      "detailItem.phone",
      "detailItem.email",
      "detailItem.latitude",
      "detailItem.longitude"
    ]),
    routeSampleId() {
      return hasIn(this.$route, "params.sampleId")
        ? this.$route.params.sampleId
        : null;
    },
    routeFarmId() {
      return hasIn(this.$route, "params.id") ? this.$route.params.id : null;
    },
    computedRules() {
      const rules = [];

      if (this.localTitleError) {
        rules.push(() => this.localTitleError);
      } else if (this.formSubmitted && this.backendErrors.title) {
        rules.push(() => this.backendErrors.title);
      } else if (this.formSubmitted && this.title.trim() == "") {
        this.shownErrorMessage = "This field is required";
        rules.push(() => this.shownErrorMessage);
      } else {
        rules.push(this.rules.required);
      }

      return rules;
    },
    onlyCountries() {
      return this.countries.map(c => c?.country_code);
    }
  },
  watch: {
    selectedClient(newVal) {
      this.client_id = newVal ? newVal.id : null;
    },
    phone(val) {
      if (!val) return;
      this.phone = val.replace(/^\+\d+\s*/, "");
    }
  },
  methods: {
    titleRules() {
      return [
        !!this.title || !!this.number || "Required when no Number is given."
      ];
    },
    numberRules() {
      return [
        !!this.number || !!this.title || "Required when no Name is given."
      ];
    },
    handleTitleInput(value) {
      this.title = value; // Normal behavior

      // Clear backend error if user is typing
      if (this.backendErrors.title) {
        this.backendErrors.title = "";
      }

      // If empty, set custom required error immediately
      if (value.trim() === "") {
        this.localTitleError = "This field is required";
      } else {
        this.localTitleError = "";
      }
    }
  },
  mounted() {
    this.client_id = this.selectedClient?.id ?? null;
  }
};
</script>
<style scoped>
v-row {
  flex-wrap: wrap;
}
</style>
